import * as React from 'react';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { dataDisplayCustomizations } from './customizations/dataDisplay';
import { colorSchemes, typography, shadows, shape } from './themePrimitives';
import { useSettings } from '@/app/contexts/SettingContext';

interface AppThemeProps {
  children: React.ReactNode;
  /**
   * This is for the docs site. You can ignore it or remove it.
   */
  disableCustomTheme?: boolean;
  themeComponents?: ThemeOptions['components'];
}

export default function AppTheme(props: AppThemeProps) {
  const settings = useSettings();
  const { children, disableCustomTheme, themeComponents } = props;
  const defaultMode = settings?.theme === 'light' || settings?.theme === 'dark' ? settings.theme : 'dark';
  const theme = React.useMemo(() => {
    return disableCustomTheme
      ? {}
      : createTheme({
        cssVariables: {
          colorSchemeSelector: 'data-mui-color-scheme',
          cssVarPrefix: 't',
        },
        colorSchemes,
        typography,
        shadows,
        shape,
        components: {
          MuiCssBaseline: {
            styleOverrides: (theme) => ({
              // body: {
              //   background: theme.vars.palette.background.default,
              //   // color: theme.palette.text.primary,
              // },
              '::selection': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
            }),
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                marginTop:8
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: ({ theme }) => ({
                backgroundImage: 'none',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }),
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: ({ theme }) => ({
                backgroundImage: 'none',
                borderColor: theme.palette.divider,
              }),
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
            styleOverrides: {
              root: ({ theme }) => ({
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 8,
                padding: theme.spacing(1, 3),
              }),
              sizeSmall: {
                padding: "4px 10px",
                fontSize: "0.8125rem",
              },

              sizeMedium: {
                padding: "8px 24px",
                fontSize: "0.875rem",
              },

              sizeLarge: {
                padding: "12px 32px",
                fontSize: "1rem",
              },
            },
            variants: [
              {
                props: { variant: "outlined", color: "primary" },
                style: ({ theme }) => ({
                  color: alpha( '#fff', 0.8),
                  borderColor: alpha( '#fff', 0.5),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderColor: theme.palette.primary.main,
                  },
                }),
              },
              {
                props: { variant: "outlined", color: "secondary" },
                style: ({ theme }) => ({
                  color: alpha( '#fff', 0.8),
                  borderColor: alpha( '#fff', 0.5),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                    borderColor: theme.palette.secondary.main,
                  },
                }),
              },
            ],
          },
          MuiDialog: {
            styleOverrides: {
              root: {
                padding:30,
              }
            }
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: ({ theme }) => ({
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.secondary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }),
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                marginLeft: 0,
                position: 'absolute',
                bottom: -20,
              },
            },
          },
          ...dataDisplayCustomizations,
          ...themeComponents,
        }
      });
  }, [disableCustomTheme, themeComponents]);
  if (disableCustomTheme) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  return (
    <ThemeProvider theme={theme} defaultMode={defaultMode} disableTransitionOnChange>
      {children}
    </ThemeProvider>
  );
}